import { createFileRoute } from "@tanstack/react-router";
import { ArrowUpRight, Check } from "lucide-react";
import { ClosingCTA } from "@/components/landing/ClosingCTA";
import { FaqSection, faqSchemaScript, type FaqItem } from "@/components/landing/FaqSection";
import { Footer } from "@/components/landing/Footer";
import { Header } from "@/components/landing/Header";
import { PageIntro } from "@/components/landing/PageIntro";
import { ParticleBackground } from "@/components/landing/ParticleBackground";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { RelatedServices } from "@/components/landing/RelatedServices";
import mockupAsset from "@/assets/idea-generator-laptop-mockup.webp.asset.json";
import { freshnessMeta, webPageSchemaScript } from "@/lib/page-freshness";
import { serviceSchemaScripts } from "@/lib/service-schema";

const PUBLISHED = "2026-10-03T00:00:00+00:00";
const MODIFIED = "2026-10-03T00:00:00+00:00";
const TITLE = "Idea Generator for Interactive Lead Gen Tools | Square Migration";
const DESCRIPTION = "Scan your website and get tailored interactive app concepts for lead generation, sales enablement, and brand differentiation. Free tool from Square Migration.";

const FAQS: FaqItem[] = [
  {
    question: "What does the Idea Generator do?",
    answer: "You enter your website URL and the tool scans it, then suggests interactive app concepts tailored to your business: calculators, assessments, analyzers, and other lead-generation experiences your team could build.",
  },
  {
    question: "Is the Idea Generator free to use?",
    answer: "Yes. It is a free brainstorming tool. You can generate ideas on your own or bring them to a conversation about building the right one.",
  },
  {
    question: "What happens after I get my ideas?",
    answer: "Pick the concept that fits your goals and bring it to a call. We map the experience, shape it around your sales process, and build the tool your prospects actually want to use.",
  },
  {
    question: "Do I need to know anything technical to use it?",
    answer: "No. Enter your website address and the tool does the rest. The ideas are written for marketing and sales teams, not developers.",
  },
];

const POINTS = [
  "Industry-specific app concepts in seconds",
  "Positioned for your audience and funnel stage",
  "Ideas grounded in what your website already says",
  "A clear starting point for your first interactive experience",
];

export const Route = createFileRoute("/idea-generator")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://www.squaremigration.com/idea-generator" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.squaremigration.com/og-image.jpg" },
      { name: "twitter:image", content: "https://www.squaremigration.com/og-image.jpg" },
      ...freshnessMeta({ published: PUBLISHED, modified: MODIFIED }),
    ],
    links: [{ rel: "canonical", href: "https://www.squaremigration.com/idea-generator" }],
    scripts: [
      ...serviceSchemaScripts({ path: "/idea-generator", name: "Idea Generator", description: DESCRIPTION, serviceType: "Interactive idea generation tool" }),
      faqSchemaScript(FAQS),
      webPageSchemaScript({ path: "/idea-generator", name: TITLE, description: DESCRIPTION, published: PUBLISHED, modified: MODIFIED }),
    ],
  }),
  component: IdeaGeneratorPage,
});

function IdeaGeneratorPage() {
  return (
    <main className="relative">
      <ParticleBackground />
      <Header />
      <PageIntro
        eyebrow="Idea Generator"
        title="Not sure what to build? Start with your own website."
        description="The Idea Generator scans your site and suggests interactive app concepts tailored to your business, calculators, assessments, analyzers, and more. Free to use, no signup required."
        bullets={["Free brainstorming tool", "Ideas built from your website", "Ready in seconds"]}
      />

      <section className="border-t border-border/50 bg-background/95 px-6 py-16 md:px-8 md:py-24 lg:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 grid items-end gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.22em] text-primary">How It Works</p>
              <h2 className="text-3xl font-semibold leading-tight sm:text-4xl">Tailored concepts, <span className="text-primary">grounded in your business</span></h2>
            </div>
            <p className="leading-relaxed text-muted-foreground lg:text-lg">Enter your website URL and the Idea Generator reads what you already say about your business, then suggests interactive experiences your prospects would actually use before they ever talk to sales.</p>
          </div>

          <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            <div>
              <p className="mb-6 text-lg leading-relaxed text-foreground/90">It is the same starting point we use with clients. Before we design a calculator, assessment, or visualizer, we look for the question your prospects are already trying to answer. The Idea Generator gives you that first pass in seconds, so your team has something concrete to react to.</p>
              <ul className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {POINTS.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary"><Check className="h-3.5 w-3.5" aria-hidden="true" /></span>
                    {item}
                  </li>
                ))}
              </ul>
              <a href="https://build.squaremigration.com/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-primary/60 px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-primary/10">
                Brainstorm Ideas <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <p className="mt-3 text-xs text-muted-foreground">Opens the Idea Generator tool at build.squaremigration.com</p>
            </div>
            <div className="relative min-h-[320px] sm:min-h-[470px]">
              <img src={mockupAsset.url} alt="Square Migration Idea Generator shown on a laptop" className="absolute inset-0 h-full w-full object-contain" />
            </div>
          </div>
        </div>
      </section>

      <ProcessSection
        eyebrow="From Ideas to a Built Tool"
        title="Turn a brainstorm into something your prospects use."
        lead="The Idea Generator is the starting line. When a concept stands out, we shape it into a working lead-generation tool built around your expertise."
        steps={[
          { number: "01", title: "Generate Ideas", description: "Scan your website and review tailored concepts for calculators, assessments, analyzers, and more." },
          { number: "02", title: "Pick a Direction", description: "Choose the idea that matches your goals, or bring a shortlist to the conversation." },
          { number: "03", title: "Shape the Experience", description: "We map the inputs, the personalized result, and the moment a prospect becomes a lead." },
          { number: "04", title: "Build & Launch", description: "We design and build the tool, then connect it to your site and your sales process." },
        ]}
      />

      <ClosingCTA
        eyebrow="Start With Ideas"
        headline={<>What would your prospects <span className="text-primary">want to figure out</span> before they call you?</>}
        subhead="That is where the tool starts."
        body="Run your website through the Idea Generator, then bring the concepts you like to a short call. We will tell you which ones are worth building first."
        ctaLabel="Talk Through Your Ideas"
      />
      <FaqSection items={FAQS} />
      <RelatedServices current="/idea-generator" />
      <Footer />
    </main>
  );
}
